import { Plugin, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useUserContext } from '@ilteducation/ilt-auth';
import { useMyOrganizations } from '@ilteducation/my-account';
import type { SlimOrganization } from '@ilteducation/my-account-types';
import { isNative, useConfigHelper } from '@ilteducation/config';
import { getAppInfo } from '@ilteducation/app-info';

declare const StonlyWidget: any;

export type PluginOptions = {
  wid: string;
};

const plugin: Plugin<PluginOptions> = (app, { wid }) => {
  const { getOptionalRealm } = useConfigHelper();
  // On a native app a realm may not have been selected yet when the plugin is installed.
  const configRealm = getOptionalRealm();
  if (configRealm === 'fr') {
    return;
  }

  const scriptId = document.createElement('script');
  scriptId.text = `window.STONLY_WID = "${wid}";`;
  const scriptMain = document.createElement('script');
  scriptMain.text =
    '!function(s,t,o,n,l,y,w,g){s.StonlyWidget||((w=s.StonlyWidget=function(){\n' +
    '  w._api?w._api.apply(w,arguments):w.queue.push(arguments)}).scriptPath=n,w.queue=[],(y=t.createElement(o)).async=!0,\n' +
    '  (g=new XMLHttpRequest).open("GET",n+"version?v="+Date.now(),!0),g.onreadystatechange=function(){\n' +
    '  4===g.readyState&&(y.src=n+"stonly-widget.js?v="+(200===g.status?g.responseText:Date.now()),\n' +
    '  (l=t.getElementsByTagName(o)[0]).parentNode.insertBefore(y,l))},g.send())\n' +
    '  }(window,document,"script","https://stonly.com/js/widget/v2/");';
  document.head.appendChild(scriptId);
  document.head.appendChild(scriptMain);

  // Started here but never awaited before mount — Stonly must not be able to delay or break startup.
  const appInfoPromise = getAppInfo().catch((e) => {
    console.warn('Stonly: could not read app info', e);
    return undefined;
  });

  const { isInitialised, isLoggedIn, user, realm, roles, subscriptions } = storeToRefs(useUserContext());
  const { organizations, loadMyOrganizations } = useMyOrganizations();
  watch(
    [isInitialised, isLoggedIn, user, realm, roles, subscriptions],
    async () => {
      if (!isInitialised.value) {
        return;
      }

      if (!isLoggedIn.value) {
        StonlyWidget('cleanup');
      } else {
        try {
          await loadMyOrganizations();
        } catch (e) {
          console.log(`Failed to load organisations for analytics`);
          console.error(e);
        }
        const appInfo = await appInfoPromise;
        // Stonly don't support lists of values, so they have to be converted in to strings,
        StonlyWidget('identify', user.value.uid, {
          realm: realm.value,
          roles: roles.value.map((role) => role.value).join(','),
          organisationIds: organizations.value.data
            .filter((org: SlimOrganization) => org.active)
            .slice(0, 10)
            .map((org: SlimOrganization) => org.uid || 'UNKNOWN')
            .join(','),
          subscriptions: subscriptions.value.map((sub) => sub.type).join(','),
          native: isNative(),
          // In a WebView the URL is always localhost — capacitor://localhost on iOS, https://localhost
          // on Android (androidScheme: 'https') — so Stonly's URL targeting can never match a real
          // domain. These are what the integration team targets on instead.
          // `realm` above is the market (se/de/fi/no/...) — no separate property needed for it.
          platform: appInfo?.platform,
          appId: appInfo?.id,
          appVersion: appInfo?.version,
          appName: appInfo?.name,
        });
      }
    },
    { immediate: true },
  );
};

export default plugin;
